import React, {
  useEffect,
} from 'react';
import {
  FieldError,
  useForm,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import {
  PatientDTO,
} from 'dtos';
import Controller from 'core/components/FormController';
import dayjs, {
  Dayjs,
} from 'utils/dayjs';
import {
  genders,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/CreatePatientDialog/helpers';
import DatePicker from 'core/components/DateTimePicker/SingleDatePicker';
import Input from 'pages/Dashboard/components/Input';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import Select from 'core/components/Select';
import {
  validateDateIfFuture,
} from 'pages/Dashboard/utils/helper';

export type DemographicsForm = Pick<PatientDTO,
 'firstName'
  | 'middleName'
  | 'lastName'
  | 'genderDescription'
> & { dateOfBirth: Dayjs };

type Props = {
  value?: DemographicsForm | null;
  onChange: (value: DemographicsForm | null) => void;
  disabled?: boolean;
  error?: FieldError;
};

function getDatePickerValue(value: unknown) {
  return typeof value === 'string' || value instanceof Date || dayjs.isDayjs(value)
    ? value
    : undefined;
}

export default function DemographicFields({
  value,
  onChange,
  disabled,
  error,
}: Props) {
  const {
    control,
    watch,
    reset,
    formState: { isDirty },
    trigger,
  } = useForm<DemographicsForm>({
    mode: 'onChange',
    defaultValues: value ?? {},
  });

  const formValue = watch();
  useEffect(() => {
    if (isDirty) {
      reset(formValue, { keepErrors: true });
      onChange(formValue);
    }
  }, [formValue, isDirty]);

  useEffect(() => {
    if (!isNil(error)) {
      trigger();
    }
  }, [error]);

  return (
    <div className="flex gap-form-field w-full">
      <Controller
        name="firstName"
        rules={{ required: true }}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Input
            {...field}
            error={!isNil(error)}
            showErrorText={false}
            disabled={disabled}
            className="flex-1 min-w-0"
            id="first-name"
            label="First Name"
            type="text"
            inputProps={{ autoComplete: 'off' }}
          />
        )}
      />
      <Controller
        name="middleName"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            disabled={disabled}
            className="flex-1 min-w-0"
            id="insurance-pater"
            label="Middle Name"
            type="text"
            inputProps={{ autoComplete: 'off' }}
          />
        )}
      />
      <Controller
        name="lastName"
        rules={{ required: true }}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Input
            {...field}
            disabled={disabled}
            error={!isNil(error)}
            showErrorText={false}
            className="flex-1 min-w-0"
            id="insurance-last-name"
            inputProps={{ autoComplete: 'off' }}
            label="Last Name"
            type="text"
          />
        )}
      />
      <Controller
        name="dateOfBirth"
        rules={{ required: true, validate: validateDateIfFuture }}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Wrapper
            id="insured-date-of-birth"
            label="Date of birth"
            className="flex-1 min-w-0"
            error={!isNil(error)}
            showErrorText={false}
          >
            <DatePicker
              value={getDatePickerValue(field.value)}
              onChange={field.onChange}
              onBlur={field.onBlur}
              disabled={disabled}
              containerClassName="!w-full"
              inputContainerClassName="!w-full"
              className="w-full"
              disableFuture
              showDateButtons={false}
            />
          </Wrapper>
        )}
      />
      <Controller
        name="genderDescription"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <Select<string>
            {...field}
            id="gender"
            disabled={disabled}
            className="flex-1 min-w-0 text-gray-500 text-xs"
            label="Gender"
            placeholder="Select"
            options={genders}
            getLabel={(option: string) => option}
            getValue={(option: string) => option}
          />
        )}
      />
    </div>
  );
}
